import React from "react";
import { Button, Flex, SelectField, TextField } from "@aws-amplify/ui-react";
import FlexFormContainer from "../common/FlexFormContainer";
import { useForm, Controller } from "react-hook-form";
import { useLifeStyleQuery } from "./api/useLifeStyleQuery";
import { useUserQuery } from "../common/useUserQuery";
import {
  useLifeStylePostMutation,
  useLifeStylePutMutation,
} from "./api/useLifeStyleMutation";
import { zodResolver } from "@hookform/resolvers/zod";
import { TagsInput } from "react-tag-input-component";
const { z } = require("zod");

const zodRequest = z.object({
  tabacco: z.optional(
    z.string().max(255, { message: "Must be 255 or fewer characters long" })
  ),
  coffee: z.optional(
    z.string().max(255, { message: "Must be 255 or fewer characters long" })
  ),
  alcohol: z.optional(
    z.string().max(255, { message: "Must be 255 or fewer characters long" })
  ),
  diet: z.optional(
    z.string().max(255, { message: "Must be 255 or fewer characters long" })
  ),
  recreational_drugs: z.optional(
    z.string().max(255, { message: "Must be 255 or fewer characters long" })
  ),
  counseling: z.optional(
    z.string().max(255, { message: "Must be 255 or fewer characters long" })
  ),
  exercise_patterns: z.optional(
    z.string().max(255, { message: "Must be 255 or fewer characters long" })
  ),
  exercise_days: z.optional(z.array(z.string())),
  sleep_quality: z.optional(
    z.string().max(20, { message: "Must be 20 or fewer characters long" })
  ),
  sleep_average_hours: z.optional(z.number().int()),
});

export default function LifeStylePage() {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(zodRequest) });
  const { data: userData } = useUserQuery();
  const {
    data: lifestyleData,
    isLoading,
    isSuccess,
    error,
  } = useLifeStyleQuery(userData?.patientUserId);

  const lifeStylePostMutate = useLifeStylePostMutation();
  const lifeStylePutMutate = useLifeStylePutMutation();

  const onSubmit = (values) => {
    const payload = {
      patientUserId: userData?.patientUserId,
      ...values,
      exercise_days: values.exercise_days || [],
    };

    if (lifestyleData && lifestyleData?.patientUserId) {
      lifeStylePutMutate.mutate(payload);
    } else {
      lifeStylePostMutate.mutate(payload);
    }
  };

  if (isLoading) return "Loading...";

  if (error) return "An error has occurred: " + error.message;

  if (lifeStylePostMutate.isLoading || lifeStylePutMutate.isLoading)
    return "Saving...";

  if (isSuccess)
    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <FlexFormContainer headerTitle={"Lifestyle"}>
          <Controller
            name="tabacco"
            control={control}
            render={({ field }) => (
              <TextField
                minWidth="300px"
                label="Tobacco "
                placeholder="How often you consume tobacco ..."
                {...field}
                value={field.value ?? lifestyleData?.tabacco}
                errorMessage={errors.tabacco?.message}
                hasError={!!errors.tabacco}
              />
            )}
          />
          <Controller
            name="coffee"
            control={control}
            render={({ field }) => (
              <TextField
                minWidth="300px"
                label="Coffee"
                placeholder="How often you consume coffee..."
                {...field}
                value={field.value ?? lifestyleData?.coffee}
                errorMessage={errors.coffee?.message}
                hasError={!!errors.coffee}
              />
            )}
          />
          <Controller
            name="alcohol"
            control={control}
            render={({ field }) => (
              <TextField
                minWidth="300px"
                label="Alcohol"
                placeholder="How often you consume alcohol..."
                {...field}
                value={field.value ?? lifestyleData?.alcohol}
                errorMessage={errors.alcohol?.message}
                hasError={!!errors.alcohol}
              />
            )}
          />
          <Controller
            name="diet"
            control={control}
            render={({ field }) => (
              <TextField
                minWidth="300px"
                label="Diet"
                placeholder="Are you into any special type of diet..."
                {...field}
                value={field.value ?? lifestyleData?.diet}
                errorMessage={errors.diet?.message}
                hasError={!!errors.diet}
              />
            )}
          />
          <Controller
            name="recreational_drugs"
            control={control}
            render={({ field }) => (
              <TextField
                minWidth="300px"
                label="Recreational drugs"
                placeholder="Have you used recreational drugs..."
                {...field}
                value={field.value ?? lifestyleData?.recreational_drugs}
                errorMessage={errors.recreational_drugs?.message}
                hasError={!!errors.recreational_drugs}
              />
            )}
          />
          <Controller
            name="counseling"
            control={control}
            render={({ field }) => (
              <TextField
                minWidth="300px"
                label="Counseling"
                placeholder="Are you receiving counseling..."
                {...field}
                value={field.value ?? lifestyleData?.counseling}
                errorMessage={errors.counseling?.message}
                hasError={!!errors.counseling}
              />
            )}
          />
          <Controller
            name="exercise_patterns"
            control={control}
            render={({ field }) => (
              <TextField
                minWidth="300px"
                label="Exercise patterns"
                placeholder="What type of exercise routine you perform..."
                {...field}
                value={field.value ?? lifestyleData?.exercise_patterns}
                errorMessage={errors.exercise_patterns?.message}
                hasError={!!errors.exercise_patterns}
              />
            )}
          />
          <Controller
            name="exercise_days"
            control={control}
            render={({ field }) => (
              <>
                <SelectField
                  minWidth="300px"
                  label="Exercise days"
                  descriptiveText="What days do you exercise?"
                  errorMessage={errors.exercise_days?.message}
                  hasError={!!errors.exercise_days}
                  onChange={(event) => {
                    const selectedDay = event.target.value;
                    let currentTags = field.value ?? [];

                    if (!currentTags.includes(selectedDay)) {
                      currentTags = [...currentTags, selectedDay];
                      field.onChange(currentTags);
                    }
                  }}
                >
                  <option value="" disabled selected>
                    Select a day
                  </option>
                  <option value="MONDAY">Monday</option>
                  <option value="TUESDAY">Tuesday</option>
                  <option value="WEDNESDAY">Wednesday</option>
                  <option value="THURSDAY">Thursday</option>
                  <option value="FRIDAY">Friday</option>
                  <option value="SATURDAY">Saturday</option>
                  <option value="SUNDAY">Sunday</option>
                </SelectField>

                {field.value && field.value.length > 0 && (
                  <TagsInput
                    value={field.value ?? []}
                    onChange={(tags) => field.onChange(tags)}
                  />
                )}
              </>
            )}
          />

          <Controller
            name="sleep_quality"
            control={control}
            render={({ field }) => (
              <SelectField
                minWidth="300px"
                label="Sleep quality"
                placeholder="Select"
                {...field}
                value={field.value ?? lifestyleData?.sleep_quality}
                errorMessage={errors.sleep_quality?.message}
                hasError={!!errors.sleep_quality}
              >
                <option value="EXCELLENT">Excellent</option>
                <option value="AVERAGE">Average</option>
                <option value="BAD">Bad</option>
                <option value="DECLINED_TO_STATE">Declined to state</option>
              </SelectField>
            )}
          />
          <Controller
            name="sleep_average_hours"
            control={control}
            render={({ field }) => (
              <TextField
                type="number"
                minWidth="30px"
                minLength={1}
                maxLength={2}
                label="Average daily sleep hours"
                placeholder="Daily sleep hours..."
                {...field}
                value={field.value ?? lifestyleData?.sleep_average_hours}
                onChange={(value) => field.onChange(value.target.valueAsNumber)}
                errorMessage={errors.sleep_average_hours?.message}
                hasError={!!errors.sleep_average_hours}
              />
            )}
          />
          <Flex gap="18px" direction="column">
            <Button variation="primary" type="submit">
              Save
            </Button>
          </Flex>
        </FlexFormContainer>
      </form>
    );
}
